import type { Page, ACF } from 'types';
import React, { memo } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useShareImages } from 'hooks/useShareImages';
import { useWordPressContext } from 'contexts/wordpress-provider';

const MedicalWebPageJson: React.FC<
  Pick<Page, 'date' | 'modified' | 'seo' | 'title'> &
    Pick<ACF, 'name' | 'description' | 'imageObjectCaption'> & {
      siteUrl: string;
      siteDescription: string;
      stateName: string;
    }
> = memo(
  ({
    seo,
    date,
    name,
    title,
    modified,
    siteUrl,
    stateName,
    description,
    siteDescription,
    imageObjectCaption,
  }) => {
    const { trustpilotNumberReviews, trustpilotRating } = useWordPressContext();
    const { locale } = useRouter();
    const webpageImage = useShareImages({
      type: 'full',
      title,
    });

    let language = 'en-US';
    let url = siteUrl;

    if (locale === 'es') {
      language = 'es-ES';
      url = `${url}/es`;
    }

    const details = {
      '@context': 'https://schema.org/',
      '@graph': [
        {
          '@type': 'MedicalWebPage',
          '@id': seo?.canonical,
          url: seo?.canonical,
          name: name || title,
          isPartOf: { '@id': `${url}/#website` },
          mainEntity: { '@id': `${seo?.canonical}#service` },
          primaryImageOfPage: {
            '@id': `${seo?.canonical}/#primaryimage`,
          },
          image: {
            '@id': `${seo?.canonical}/#primaryimage`,
          },
          thumbnailUrl: webpageImage.url,
          datePublished: date,
          dateModified: modified,
          reviewedBy: { '@id': 'https://leafwell.com/#gavin-moreland' },
          medicalAudience: {
            '@type': 'MedicalAudience',
            audienceType: 'Patient',
          },
          specialty: 'https://schema.org/PrimaryCare',
          description: description || seo?.description,
          breadcrumb: {
            '@id': `${seo?.canonical}/#breadcrumb`,
          },
          inLanguage: language,
          potentialAction: [
            {
              '@type': 'ReadAction',
              target: [seo?.canonical],
            },
          ],
        },
        {
          '@type': 'ImageObject',
          inLanguage: language,
          '@id': `${seo?.canonical}/#primaryimage`,
          url: webpageImage.url,
          contentUrl: webpageImage.url,
          width: webpageImage.width,
          height: webpageImage.height,
          caption: imageObjectCaption || title,
        },
        {
          '@type': 'BreadcrumbList',
          '@id': `${seo?.canonical}/#breadcrumb`,
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: url,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: stateName,
            },
          ],
        },
        {
          '@type': 'WebSite',
          '@id': `${url}/#website`,
          url: url,
          name: 'Leafwell',
          description: siteDescription,
          publisher: {
            '@id': 'https://leafwell.com/#organization',
          },
          potentialAction: [
            {
              '@type': 'SearchAction',
              target: {
                '@type': 'EntryPoint',
                urlTemplate: 'https://leafwell.com/?s={search_term_string}',
              },
              'query-input': 'required name=search_term_string',
            },
          ],
          inLanguage: language,
        },
        {
          '@type': 'Person',
          '@id': 'https://leafwell.com/#gavin-moreland',
          name: 'Gavin Moreland',
          honorificSuffix: 'MD',
          jobTitle: 'Medical Director',
          description:
            'Board-certified Emergency Medicine physician with over two decades of clinical experience, serving as Medical Director at Leafwell.',
          url: 'https://leafwell.com/blog/authors/dr-gavin-moreland',
          image:
            'https://admin.leafwell.com/app/uploads/2025/10/Gavin-Moreland-headshot-for-bio.png',
          worksFor: { '@id': 'https://leafwell.com/#organization' },
          alumniOf: [
            { '@type': 'CollegeOrUniversity', name: 'Indiana University' },
            {
              '@type': 'CollegeOrUniversity',
              name: 'Louisiana State University, New Orleans School of Medicine',
            },
          ],
          memberOf: [
            { '@type': 'Organization', name: 'American Medical Association' },
            {
              '@type': 'Organization',
              name: 'American College of Emergency Physicians',
            },
            {
              '@type': 'Organization',
              name: 'Society for Academic Emergency Medicine',
            },
          ],
          hasCredential: {
            '@type': 'EducationalOccupationalCredential',
            credentialCategory: 'Board Certification',
            name: 'Board-Certified in Emergency Medicine',
          },
          knowsAbout: [
            'Medical cannabis',
            'Emergency medicine',
            'Chronic pain',
          ],
          sameAs: ['https://www.linkedin.com/in/gavin-moreland-87ab03ba/'],
        },
        {
          '@type': 'MedicalOrganization',
          '@id': 'https://leafwell.com/#organization',
          name: 'Leafwell',
          url: 'https://leafwell.com',
          description: siteDescription,
          medicalSpecialty: 'https://schema.org/PrimaryCare',
          logo: {
            '@type': 'ImageObject',
            inLanguage: language,
            '@id': 'https://www.leafwell.com/#/schema/logo/image/',
            url: 'https://admin.leafwell.com/app/uploads/2021/10/leafwell-icon.jpg',
            contentUrl:
              'https://admin.leafwell.com/app/uploads/2021/10/leafwell-icon.jpg',
            width: 512,
            height: 512,
            caption: 'Leafwell',
          },
          ...(trustpilotRating &&
            trustpilotNumberReviews && {
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: trustpilotRating,
                ratingCount: trustpilotNumberReviews,
                bestRating: 5,
                worstRating: 1,
              },
            }),
          sameAs: [
            'https://www.facebook.com/leafwellofficial/',
            'https://twitter.com/_Leafwell_',
            'https://www.instagram.com/leafwell_official/',
            'https://www.linkedin.com/company/leafwellofficial/',
            'https://www.pinterest.com/leafwell_official/',
            'https://www.youtube.com/channel/UCok2ps-pUDEA885yqTaZLgA/',
            'https://www.trustpilot.com/review/leafwell.com',
          ],
        },
      ],
    };

    return React.createElement(
      Head,
      null,
      React.createElement('script', {
        type: 'application/ld+json',
        dangerouslySetInnerHTML: {
          __html: JSON.stringify(details),
        },
      }),
    );
  },
);

export default MedicalWebPageJson;
